import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../../config';

const API = `${BACKEND_URL}/api`;
const ACHIEVEMENT_ID = 'authorization-quiz';

const AuthorizationQuiz = () => {
  const navigate = useNavigate();
  const [showOffer, setShowOffer] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [resolved, setResolved] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 2;
  const correctPatternId = 'B';

  const patterns = [
    { id: 'A', label: 'PATTERN A', waves: '▁▃▅▇█▇▅▃▁' },
    { id: 'B', label: 'PATTERN B', waves: '▁▁██▁▁██▁', correct: true },
    { id: 'C', label: 'PATTERN C', waves: '█▇▅▃▁▃▅▇█' },
    { id: 'D', label: 'PATTERN D', waves: '▃▃▃███▃▃▃' }
  ];

  const handlePatternClick = async (patternId) => {
    if (resolved) return;
    
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    setSelectedPattern(patternId);

    if (patternId === correctPatternId) {
      setResolved(true);
      setStatusText('Waveform match confirmed. Authorization granted.');
      
      // Store achievement in sessionStorage
      sessionStorage.setItem('pendingAchievement', ACHIEVEMENT_ID);

      setTimeout(() => {
        setShowOffer(true);
        setTimeout(() => {
          navigate(`/scan?achievement=${ACHIEVEMENT_ID}`);
        }, 1300);
      }, 1100);
    } else {
      if (newAttempts < maxAttempts) {
        setStatusText('Waveform mismatch. Look for the square wave pattern (high-low-high-low).');
      } else {
        setResolved(true);
        setSelectedPattern(correctPatternId);
        setStatusText('Authentication override. System accepted square wave pattern.');
        
        sessionStorage.setItem('pendingAchievement', ACHIEVEMENT_ID);
        
        setTimeout(() => {
          setShowOffer(true);
          setTimeout(() => {
            navigate(`/scan?achievement=${ACHIEVEMENT_ID}`);
          }, 1300);
        }, 1200);
      }
    }
  };

  return (
    <div style={styles.wrap}>
      {!showOffer ? (
        <div style={styles.terminal}>
          <div style={styles.header}>
            <div style={styles.dots}>
              <span style={styles.dot}></span>
              <span style={styles.dot}></span>
              <span style={styles.dot}></span>
            </div>
            <div style={styles.headerLabel}>BIOMETRIC AUTHORIZATION</div>
          </div>
          
          <div style={styles.body}>
            <div style={styles.line}>
              <span style={styles.label}>SYSTEM:</span>
              <span style={styles.value}> WAVEFORM AUTHENTICATION v1.9</span>
            </div>
            <div style={styles.line}>
              <span style={styles.label}>USER:</span>
              <span style={styles.value}> UNKNOWN / UNAUTHORIZED</span>
            </div>
            <div style={styles.line}>
              <span style={styles.label}>REQUIRED:</span>
              <span style={styles.value}> SQUARE WAVE SIGNATURE MATCH</span>
            </div>
            <div style={styles.line}>
              <span style={styles.label}>STATUS:</span>
              <span style={{...styles.value, color: '#ef4444'}}> AWAITING INPUT</span>
            </div>
            
            <div style={styles.gameTitle}>Match The Authorization Waveform</div>
            <div style={styles.gameInstruction}>
              Select the <strong>square wave pattern</strong> to pass authentication.
            </div>
            
            <div style={styles.referenceWave}>
              <div style={styles.refLabel}>TARGET SIGNATURE:</div>
              <div style={styles.refWave}>▁▁██▁▁██▁</div>
            </div>
            
            <div style={styles.patternsGrid}>
              {patterns.map((pattern) => (
                <div
                  key={pattern.id}
                  style={{
                    ...styles.pattern,
                    ...(selectedPattern === pattern.id && resolved ? styles.selected : {}),
                    ...(resolved && pattern.id !== correctPatternId ? styles.disabled : {})
                  }}
                  onClick={() => handlePatternClick(pattern.id)}
                >
                  <div style={styles.patternLabel}>{pattern.label}</div>
                  <div style={styles.patternWave}>{pattern.waves}</div>
                </div>
              ))}
            </div>
            
            {statusText && (
              <div style={{
                ...styles.statusText,
                color: resolved ? '#10b981' : '#ef4444'
              }}>
                {statusText}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={styles.offer}>
          <small style={{color: '#10b981', fontWeight: 700}}>AUTHENTICATION COMPLETE</small>
          <div style={styles.offerTitle}>Authorization granted.</div>
          <p>Waveform matched successfully. Your <strong>exclusive Tappd offer</strong> is unlocked.</p>
          <p style={styles.tagline}>Accessing secure file…</p>
        </div>
      )}
    </div>
  );
};

const styles = {
  wrap: { minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: '"Courier New", monospace' },
  terminal: { background: '#1e293b', borderRadius: '12px', maxWidth: '600px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid #334155' },
  header: { background: '#334155', padding: '12px 16px', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px' },
  dots: { display: 'flex', gap: '6px' },
  dot: { width: '12px', height: '12px', borderRadius: '50%', background: '#64748b' },
  headerLabel: { color: '#e2e8f0', fontSize: '14px', fontWeight: 700, letterSpacing: '1px' },
  body: { padding: '24px', color: '#10b981', fontSize: '14px', lineHeight: '1.8' },
  line: { marginBottom: '8px' },
  label: { color: '#fbbf24', fontWeight: 700 },
  value: { color: '#10b981' },
  gameTitle: { color: '#fbbf24', fontSize: '18px', fontWeight: 700, marginTop: '24px', marginBottom: '12px', textTransform: 'uppercase' },
  gameInstruction: { color: '#cbd5e1', fontSize: '14px', marginBottom: '20px' },
  referenceWave: { background: '#0f172a', padding: '20px', borderRadius: '8px', border: '2px solid #10b981', marginBottom: '20px', textAlign: 'center' },
  refLabel: { color: '#10b981', fontSize: '12px', fontWeight: 700, marginBottom: '8px' },
  refWave: { fontSize: '36px', color: '#10b981', letterSpacing: '2px' },
  patternsGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px' },
  pattern: { padding: '20px', background: '#0f172a', border: '2px solid #334155', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center' },
  selected: { borderColor: '#10b981', background: 'rgba(16, 185, 129, 0.1)' },
  disabled: { opacity: 0.4, cursor: 'not-allowed' },
  patternLabel: { color: '#fbbf24', fontSize: '12px', fontWeight: 700, marginBottom: '12px' },
  patternWave: { fontSize: '28px', color: '#cbd5e1', letterSpacing: '2px' },
  statusText: { fontSize: '14px', fontWeight: 600, textAlign: 'center', marginTop: '16px' },
  offer: { maxWidth: '600px', background: 'white', padding: '40px', borderRadius: '12px', textAlign: 'center', color: '#1e293b' },
  offerTitle: { fontSize: '28px', fontWeight: 700, margin: '16px 0', fontFamily: 'Fraunces, serif' },
  tagline: { color: '#64748b', fontStyle: 'italic', marginTop: '16px' },
};

export default AuthorizationQuiz;